import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { kCitatorGetOpinionCitationLinksApiPathV2 } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { OpinionCitationLinksResponseSchema, type OpinionCitationLinksResponse } from '@/citator/schema/opinion-citation-link-schema'
import { readResponseJson, getResponseDebugContext, formatUnknownError } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { isAbortError } from '@/util/errors/is-abort-error'

export async function getOpinionCitationLinks(parentId: string, signal?: AbortSignal): Promise<OpinionCitationLinksResponse> {
  const sentryTransactionId = nanoid()
  const requestUrl = `${kPaxtonAppApiRESTBaseUrl()}/${kCitatorGetOpinionCitationLinksApiPathV2}/${encodeURIComponent(parentId)}`

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error('User not authenticated')

    const response = await fetch(requestUrl, {
      method: 'GET',
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentryTransactionId,
      },
    })
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const detail = !parseError && data && typeof data === 'object' && 'detail' in data ? String(data.detail) : `Request failed with status ${response.status}`
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Opinion citation links request failed'), {
          extra: {
            ...getResponseDebugContext(response, requestUrl),
            parentId,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, detail, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Case citation links response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, requestUrl),
            parentId,
            parseError: formatUnknownError(parseError),
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid opinion citation links response JSON')
    }

    const parsed = OpinionCitationLinksResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Opinion citation links response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, requestUrl),
            parentId,
            issues: parsed.error.issues,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid opinion citation links response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (isAbortError(error)) throw error
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, { extra: { parentId } })
      })
    }
    throw error
  }
}
